'use client'
import AgeVerificationModal from '@/app/[locale]/[roomId]/components/AgeVerificationModal'
import VramAlertModal from '@/app/[locale]/[roomId]/components/VramAlertModal'
import { IFRAME_SEARCH_PARAM_KEYS } from '@/constants/iframe'
import { useAuthContext } from '@/context/AuthContext'
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useUAContext } from '@/context/UAContext'
import { AppConfig, DeviceToVram, DeviceType } from '@/types/configs'
import { useRouter, useSearchParams } from 'next/navigation'
import { ReactNode, useEffect, useState } from 'react'

//check flow :  voucher room ->  device -> age -> vram

function isVramOverLimit(
  appConfig: AppConfig,
  deviceType: DeviceType,
  roomVramKb: number | undefined,
  vramLimitKb: number | undefined,
  vramLimitPassed: boolean,
) {
  if (!roomVramKb || !vramLimitKb || vramLimitPassed) return false
  if (deviceType === 'desktop') {
    const alertVramKb =
      appConfig?.features?.device_to_vram?.find((d: DeviceToVram) => d.device === 'desktop')
        ?.alert_vram_kb ?? -1
    return roomVramKb > alertVramKb
  } else {
    return roomVramKb > vramLimitKb
  }
}

type CheckEnterRoomScreenProps = {
  children: ReactNode
  isTrailer?: boolean
}

function CheckEnterRoomScreen({ children }: CheckEnterRoomScreenProps) {
  const {
    roomData,
    isPreferredDevice,
    setIsTrailer,
    needAgeVerification,
    passAgeVerification,
    isVoucherCheckPassed,
    isPremiumRoom,
  } = useCheckEnterRoomContext()
  const { deviceType, totalVramKb: devicesVramLimit } = useUAContext()
  const { store, appConfig } = useConfigsContext()
  const { isCheckingAuth } = useAuthContext()
  const router = useRouter()
  const searchParams = useSearchParams()
  const vramLimitPassed = searchParams.has(IFRAME_SEARCH_PARAM_KEYS.VRAM_LIMIT_PASSED)

  const showDeviceAlert = !isPreferredDevice
  const [showVramAlert, setShowVramAlert] = useState(() =>
    isVramOverLimit(
      appConfig,
      deviceType,
      roomData?.total_vram_kb,
      devicesVramLimit,
      vramLimitPassed,
    ),
  )

  const leaveWorld = () => {
    router.push('/')
  }

  useEffect(() => {
    if (!store) return

    if (isVoucherCheckPassed === false || (showDeviceAlert && !needAgeVerification)) {
      setIsTrailer(true)
      setShowVramAlert(false)
    } else if (isVoucherCheckPassed) {
      setIsTrailer(false)
    }
  }, [
    store,
    showDeviceAlert,
    setIsTrailer,
    needAgeVerification,
    isPremiumRoom,
    isVoucherCheckPassed,
  ])
  if (isCheckingAuth || !store || isVoucherCheckPassed === undefined) return

  if (needAgeVerification) {
    return <AgeVerificationModal onCancel={leaveWorld} onAction={passAgeVerification} />
  } else if (showVramAlert) {
    return (
      <VramAlertModal
        onCancel={() => setShowVramAlert(false)}
        onAction={() => {
          setShowVramAlert(false)
          setIsTrailer(true)
        }}
      />
    )
  }
  return <>{children}</>
}

export default CheckEnterRoomScreen
